import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '@coin/shared/auth/data-access';
import { FrontendType, PermissionResource } from '@coin/shared/core/util-enums';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { firstValueFrom, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { PermissionsService } from './permissions.service';
import { UserState } from './user.state';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  private authService = inject(AuthService);
  private userState = inject(UserState);
  private router = inject(Router);
  private permissionsService = inject(PermissionsService);

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    if (this.authService.isE2eTest$.value) {
      while (!(await this.authService.getEmulation())) {
        await TinyHelpers.sleep(20);
      }
    }

    if (this.authService.isAuthenticated$.value) {
      const requests$ = this.authService.isE2eTest$.value
        ? [this.userState.loadUser(true)]
        : [this.userState.loadUser(), this.userState.loadLoggedInUser()];
      return firstValueFrom(
        forkJoin(requests$).pipe(
          map(res => {
            const skipFrontendTypeCheck = this.skipFrontendTypeCheck(
              route.data?.skipFrontendTypeCheckForRoles,
              route.data.frontendType
            );
            if (skipFrontendTypeCheck) {
              return !!res;
            }

            if (!this.hasPermissions(route.data.frontendType ?? FrontendType.admin)) {
              this.router.navigate(['unauthorized']);
              return false;
            }
            return !!res;
          })
        )
      );
    }
    this.authService.login(state.url);
    return false;
  }

  // Allows users, that do not have the PermissionResource.AdminApp to access the according route.
  // Only the permission for the specific route is validated. Returns true if the user has all permissions of the
  // skipFrontendTypeCheckForRoles property in the admin routing module. Currently only in place for admin.
  private skipFrontendTypeCheck(roles: PermissionResource[], frontendType: FrontendType): boolean {
    return frontendType === FrontendType.admin && roles && this.permissionsService.hasPermissions(...roles);
  }

  private hasPermissions(frontendType: FrontendType): boolean {
    const frontendTypeResource =
      frontendType === FrontendType.admin ? PermissionResource.AdminApp : PermissionResource.CustomerApp;
    return this.permissionsService.hasPermissions(frontendTypeResource);
  }
}
